/**
 * Drag and drop between the bag, the vault and the paper doll.
 *
 * The **decision** half lives here and is pure, so every rule about what a gesture
 * means is testable without a pointer, a layout or a container. jsdom computes no
 * layout, so a resolver that needed real element rectangles would be untestable in
 * either suite — which is precisely why the component keeps only the pointer plumbing
 * and this file keeps the meaning.
 *
 * ── One gesture, several commands ───────────────────────────────────────────
 *
 * A drop resolves to an *intent*, not to a command, because two of the six cases need
 * two commands: swapping rings is `CMD_UNEQUIP_ITEM` then `CMD_EQUIP_ITEM`, and both
 * are needed because `CMD_EQUIP_ITEM` takes from the bag by design. Commands arrive in
 * order on one socket, so the pair is safe — and if the first is refused (a full bag),
 * the second is refused too and nothing happens. `commandsFor` is the one place that
 * expansion lives.
 */

import {
  equipItem,
  moveItem,
  placementOf,
  unequipItem,
  type Command,
  type ContainerId,
  type DollSlot,
  type InventoryItem,
} from '../net/protocol';

/** Where a dragged item came from. */
export type DragOrigin =
  | { kind: 'container'; container: ContainerId }
  | { kind: 'doll'; slot: DollSlot };

/** A drag in progress. */
export interface Drag {
  item: InventoryItem;
  origin: DragOrigin;
  /**
   * Which cell of the item the pointer grabbed. Zero for a doll slot, which has no
   * cells — an equipped item has a slot, not an area.
   */
  grab: { col: number; row: number };
  /**
   * Where the pointer is, in viewport coordinates, for the ghost.
   *
   * The only pixels on this type. There used to be a `cell` field too — the grid's cell
   * size, so the ghost could be drawn to match — and it is gone: the ghost is sized in
   * CSS from `--cell`, the same token the grid uses, so the two cannot disagree and both
   * scale with the root. A pixel constant in TypeScript would have had to be scaled in
   * step with a media query, which is exactly the kind of pairing that drifts.
   */
  x: number;
  y: number;
}

/**
 * Where a drop landed.
 *
 * **A cell is always the bag**, and that is not shorthand — the bag is the only
 * container with cells. The vault is a list, so landing on it has no coordinates at
 * all, which is why it is a target of its own rather than a `container` field on
 * `cell`. The old shape could express "the vault, at 3,4", and the resolver had to
 * carry that impossible case as far as the wire.
 */
export type DropTarget =
  | { kind: 'cell'; col: number; row: number }
  /** The vault's list, anywhere in it — order is the viewer's, not the item's. */
  | { kind: 'vault' }
  | { kind: 'doll'; slot: DollSlot }
  /** Anywhere else: the world, a panel's chrome, outside the window. */
  | { kind: 'elsewhere' };

/**
 * Why a drop did nothing, when the client can say so without asking.
 *
 * These are answered locally for the reason a refused *dodge* is: the round trip is
 * 50–100 ms of the player wondering whether the gesture worked, and the client already
 * holds every fact needed. The server refuses the same gestures anyway if a modified
 * client sends them.
 */
export type DropRefusal =
  /** Released over nothing. The commonest outcome, and not an error. */
  | 'CANCELLED'
  /** A worn item dragged straight into the vault, or a vaulted item onto the doll. */
  | 'VAULT_IS_NOT_A_WARDROBE'
  /** Dropped where it already was. */
  | 'NO_MOVEMENT';

/** What a drop means. */
export type DropIntent =
  | { kind: 'move'; uuid: string; container: ContainerId; col: number; row: number }
  | { kind: 'equip'; uuid: string; slot: DollSlot }
  | { kind: 'unequip'; slot: DollSlot }
  /** A worn item moved to another doll slot: off, then on. */
  | { kind: 'reslot'; uuid: string; from: DollSlot; to: DollSlot }
  | { kind: 'nothing'; reason: DropRefusal };

/**
 * What a drop means, given where it started and where it landed.
 *
 * Every branch is a decision somebody has to be able to argue with, so they are all
 * here rather than spread across two components:
 *
 *   - **A cell in a container** is a move. The same container is a rearrangement; the
 *     other is a deposit or a withdrawal. The server refuses either outside town.
 *   - **A doll slot, from the bag**, is an equip. From the *vault* it is refused
 *     locally: `CMD_EQUIP_ITEM` takes from the bag by design, so this would be a
 *     round trip that could only end in silence.
 *   - **A container cell, from the doll**, is an unequip — and the **cell is
 *     ignored**, because the server first-fits an unequipped item and there is no
 *     command that says where. Aiming at a particular hole and having it land
 *     elsewhere is a real wart; it is the honest one, because inventing a
 *     `CMD_UNEQUIP_TO_CELL` for it would be a wire change for a nicety.
 *   - **Another doll slot, from the doll**, is a reslot: two commands, in order.
 *   - **The vault, from the doll**, is refused: unequip goes to the bag, and a worn
 *     item is not something you deposit in one gesture.
 */
export function resolveDrop(drag: Drag, target: DropTarget): DropIntent {
  if (target.kind === 'elsewhere') {
    return { kind: 'nothing', reason: 'CANCELLED' };
  }

  if (drag.origin.kind === 'doll') {
    if (target.kind === 'doll') {
      if (target.slot === drag.origin.slot) {
        return { kind: 'nothing', reason: 'NO_MOVEMENT' };
      }
      return { kind: 'reslot', uuid: drag.item.uuid, from: drag.origin.slot, to: target.slot };
    }
    if (target.kind === 'vault') {
      return { kind: 'nothing', reason: 'VAULT_IS_NOT_A_WARDROBE' };
    }
    return { kind: 'unequip', slot: drag.origin.slot };
  }

  if (target.kind === 'doll') {
    if (drag.origin.container === 'VAULT') {
      return { kind: 'nothing', reason: 'VAULT_IS_NOT_A_WARDROBE' };
    }
    return { kind: 'equip', uuid: drag.item.uuid, slot: target.slot };
  }

  if (target.kind === 'vault') {
    // **Already in the vault is nothing**, not a rearrangement: the list has no places
    // to be moved between, and the order the player is looking at is one they chose in
    // the panel. The server answers such a move with `NO_ARRANGEMENT`; this stops it
    // ever being asked.
    if (drag.origin.container === 'VAULT') {
      return { kind: 'nothing', reason: 'NO_MOVEMENT' };
    }
    // A deposit names no cell. The zeroes are what the wire requires and what the
    // server ignores — see `moveItem` in internal/game/container.go.
    return { kind: 'move', uuid: drag.item.uuid, container: 'VAULT', col: 0, row: 0 };
  }

  // **Dropped exactly where it started. This is what a plain click is**, and reading it
  // as a move rather than as nothing was a real fault: pressing and releasing on one
  // cell sent a `CMD_MOVE_ITEM` to the cell the item was already in — a wasted command
  // and a wasted frame on every click in the bag — and the panel's own click handler
  // fired on top of it, so a click both moved and equipped.
  if (drag.origin.container === 'BAG') {
    const at = placementOf(drag.item);
    if (at.col === target.col && at.row === target.row) {
      return { kind: 'nothing', reason: 'NO_MOVEMENT' };
    }
  }

  // A cell, so the bag: either a rearrangement or a withdrawal.
  return {
    kind: 'move',
    uuid: drag.item.uuid,
    container: 'BAG',
    col: target.col,
    row: target.row,
  };
}

/**
 * The commands one intent expands to, in the order they must be sent.
 *
 * The only place a gesture becomes more than one command. A reslot is two, and they
 * are ordered: taking the item off puts it in the bag, which is where `CMD_EQUIP_ITEM`
 * looks for it. If the bag has no room the first is refused and the second finds
 * nothing — so the outcome of a failed reslot is "nothing happened", never "the item
 * is on the floor".
 */
export function commandsFor(intent: DropIntent): Command[] {
  switch (intent.kind) {
    case 'move':
      return [moveItem(intent.uuid, intent.container, intent.col, intent.row)];
    case 'equip':
      return [equipItem(intent.uuid, intent.slot)];
    case 'unequip':
      return [unequipItem(intent.slot)];
    case 'reslot':
      return [unequipItem(intent.from), equipItem(intent.uuid, intent.to)];
    case 'nothing':
      return [];
  }
}

/** What to tell the player when the client refused a drop itself. */
export function dropRefusalText(reason: DropRefusal): string | null {
  switch (reason) {
    case 'CANCELLED':
    case 'NO_MOVEMENT':
      // Not failures. Releasing over nothing, or over the square you started on, is
      // how a drag is *cancelled* — saying something about it would put a message on
      // screen every time the player changed their mind.
      return null;
    case 'VAULT_IS_NOT_A_WARDROBE':
      return 'Move it to your bag first — you cannot wear straight out of the vault.';
  }
}

/**
 * What to tell the player when the **server** refused a placement.
 *
 * A separate vocabulary from the three above, and separate wording per container, for
 * the reason `beltRefusalText` is separate from `refusalText`: the reason keys overlap
 * between vocabularies and the same key needs a different sentence in each. `NO_SPACE`
 * in a bag ends a dive; in a vault it ends a season.
 */
export function containerRefusalText(reason: string, container?: ContainerId): string {
  switch (reason) {
    case 'NO_SPACE':
      return container === 'VAULT'
        ? 'The vault is full. Scrap something, or make room.'
        : 'No room in your bag for that shape. Scrap something, or store it in town.';
    case 'NOT_YOURS':
      // A monster rolls one drop per player who was in the fight. The label is already
      // dimmed, so reaching this is either a modified client or a very fast finger — and
      // saying whose it is would be more useful only if names were on the wire, which they
      // are not for a ground item.
      return 'That drop belongs to someone else. Yours is on the floor too.';
    case 'BAD_CELL':
      return 'Nothing fits there.';
    case 'LOCKED':
      return 'That item is locked. Alt-click to unlock it first.';
    case 'NOT_IN_TOWN':
      return 'The vault is in town. Take a portal home to reach it.';
    case 'UNKNOWN_ITEM':
      return 'That item is no longer where you left it.';
    case 'UNKNOWN_CONTAINER':
      // Unreachable from this client, which only ever names one of the two. Worded
      // anyway: an unworded reason is the failure this whole family exists for.
      return 'There is nowhere to put that.';
    case 'NO_ARRANGEMENT':
      // Also unreachable — `resolveDrop` reads a vault-to-vault drop as nothing — and
      // worded for the same reason. If it ever appears, the two halves have drifted
      // about what the vault is.
      return 'The vault is a list, not a grid — sort it instead.';
    case 'DEAD':
      return 'Not while dead.';
    default:
      return 'That could not be moved.';
  }
}
